import { Affix, Button, Card, Form, Space, Spin } from "antd";
import { IOU_COST_LIST_ENDPOINT } from "apiServices/API_ENDPOINTS";
import { getData } from "apiServices/common";
import HandleNotification from "common/Notification";
import { isMobileView } from "components/Common/AppHelper";
import Header from "components/Common/Header";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import IOUCostForm from "../IOUCostForm";
import { checkPermission } from "../config/checkPermission";
import { ExclamationCircleOutlined } from "@ant-design/icons";

const UpdateIOUCost = (props) => {
  const { width, viewMode, mode, isEdit } = props;
  console.log("🚀 ~ UpdateIOUCost ~ isEdit:", isEdit);
  console.log("🚀 ~ UpdateIOUCost ~ viewMode:", viewMode);
  const [editInfo, setEditInfo] = useState({});
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { iouCostId } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  const loggedInUserId = parseInt(localStorage.getItem("user_id"), 10);
  const loggedInUserRole = localStorage.getItem("user_type");
  const isCreator = loggedInUserId === editInfo?.user?.id;

  const canUpdate = checkPermission(
    loggedInUserRole,
    editInfo?.status,
    isCreator,
    "update",
    editInfo.department_approve,
    editInfo.director_approve,
    editInfo?.account_approve
  );
  const canView = checkPermission(
    loggedInUserRole,
    editInfo?.status,
    isCreator,
    "view",
    editInfo.department_approve,
    editInfo.director_approve,
    editInfo?.account_approve
  );
  console.log("🚀 ~ UpdateIOUCost ~ canUpdate:", canUpdate);

  // Get current user info from Redux store
  const currentUser = useSelector((state) => state.auth?.user);

  const getIOUCostInfo = useCallback(async (id) => {
    setIsLoading(true);
    const query = `${IOU_COST_LIST_ENDPOINT}/${id}`;
    const response = await getData(query);

    if (response && response?.status === 200) {
      const iouCostData = response?.data?.data;
      setEditInfo(iouCostData);
    } else {
      HandleNotification(
        "error",
        "Error!",
        response?.message || "Failed to get IOU data",
      );
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (iouCostId) {
      getIOUCostInfo(iouCostId);
    }
  }, [iouCostId, getIOUCostInfo]);

  const discardForm = () => {
    navigate("/import-export-expense-tracking/iou-cost");
  };

  const updateIOUCost = () => {
    form.submit();
  };

  const ActionUI = () => {
    return (
      <Space>
        <Button size="small" key={1} danger onClick={() => discardForm()}>
          {viewMode ? "Back" : "Discard"}
        </Button>

        {canUpdate ? (
          <Button
            size="small"
            key={2}
            type="primary"
            onClick={() => updateIOUCost()}
            hidden={viewMode}
          >
            Update IOU Request
          </Button>
        ) : null}
      </Space>
    );
  };
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "90vh",
          backgroundColor: "#fff",
        }}
      >
        <Spin spinning={isLoading} />
      </div>
    );
  }

  return (
    <>
      <Affix offsetTop={0}>
        <Card
          className="p-fixed"
          style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}
        >
          <Header
            title={`${viewMode ? "View" : "Edit"} IOU Request`}
            subTitle=""
            addLink="/"
            addText="Add New"
            actions={<ActionUI />}
          />
        </Card>
      </Affix>

      {canUpdate && !viewMode ? (
        <IOUCostForm
          form={form}
          isEdit={true}
          viewMode={viewMode}
          editInfo={editInfo}
          currentUser={currentUser}
          mode={mode}
          canUpdate={canUpdate}
        />
      ) : canView && viewMode ? (
        <IOUCostForm
          form={form}
          isEdit={true}
          viewMode={true}
          editInfo={editInfo}
          currentUser={currentUser}
          mode={mode}
        />
      ) : (
        <Card
          style={{
            marginTop: 20,
            width: `${isMobileView() ? "100%" : `${width}px`}`,
            textAlign: "center",
            padding: "32px 24px",
            border: "none",
            borderRadius: "12px",
            background: "linear-gradient(135deg, #fff9e6 0%, #fff4d9 100%)",
            boxShadow:
              "0 4px 16px rgba(250, 173, 20, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Decorative background elements */}
          <div
            style={{
              position: "absolute",
              top: -50,
              right: -50,
              width: 150,
              height: 150,
              borderRadius: "50%",
              background: "rgba(250, 173, 20, 0.05)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              bottom: -30,
              left: -30,
              width: 100,
              height: 100,
              borderRadius: "50%",
              background: "rgba(250, 173, 20, 0.03)",
              pointerEvents: "none",
            }}
          />

          {/* Icon container with animated background */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "rgba(250, 173, 20, 0.1)",
              marginBottom: 16,
              position: "relative",
            }}
          >
            <ExclamationCircleOutlined
              style={{
                fontSize: "28px",
                color: "#E7404E",
              }}
            />
          </div>

          {/* Title */}
          <div
            style={{
              fontSize: "24px",
              fontWeight: 700,
              color: "#E7404E",
              marginBottom: 12,
              letterSpacing: "-0.01em",
            }}
          >
            Permission Denied
          </div>

          {/* Description */}
          <p
            style={{
              margin: 0,
              fontSize: "16px",
              color: "#8c8c8c",
              lineHeight: "1.6",
              maxWidth: 400,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            You do not have permission to edit this IOU Cost!
          </p>
        </Card>
      )}
    </>
  );
};

export default UpdateIOUCost;
